import {
  Linkedin,
  Youtube,
  Instagram,
  ShoppingBag,
  MapPin,
  MessageCircle,
  Play,
  ArrowUpRight,
} from "lucide-react";
import { Reveal } from "../lib/motion";
import { EMBLEM_STORY, TRUSTED_LINKS, FEATURED_VIDEO, IMAGES } from "../data";

const ICONS = { Linkedin, Youtube, Instagram, ShoppingBag, MapPin, MessageCircle };

export function BrandStory() {
  return (
    <section id="emblem" className="bg-[#111] py-24 md:py-40 grain relative overflow-hidden">
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-[0.85fr_1.15fr] gap-16 lg:gap-24 items-center">
        <Reveal className="flex flex-col items-center lg:items-start">
          <div
            className="relative w-64 h-64 md:w-80 md:h-80 rounded-full flex items-center justify-center"
            style={{
              background:
                "radial-gradient(circle, rgba(176,141,87,0.22) 0%, rgba(176,141,87,0.05) 45%, transparent 72%)",
            }}
          >
            <img
              src="/agraga-crest.png"
              alt="AGRAGA Velora emblem"
              className="w-56 h-56 md:w-72 md:h-72 object-contain"
              style={{ filter: "drop-shadow(0 14px 46px rgba(176,141,87,0.3))" }}
            />
          </div>
        </Reveal>

        <div>
          <Reveal>
            <p className="overline text-[#B08D57] mb-6">The Emblem</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="section-title font-display text-[#F8F7F3] mb-4">
              A Mark That Carries a Promise
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="font-body text-white/60 text-lg leading-relaxed max-w-[560px] mb-12">
              Our crest is not decoration. Every element is a vow — read it and you read
              who we are.
            </p>
          </Reveal>

          <div className="space-y-8">
            {EMBLEM_STORY.map((e, i) => (
              <Reveal key={e.symbol} delay={0.1 + i * 0.06}>
                <div className="grid grid-cols-[auto_1fr] gap-5 border-b border-white/10 pb-7">
                  <span className="font-display text-[#B08D57] text-2xl leading-none pt-1">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h3 className="font-head font-bold text-[#F8F7F3] text-lg mb-2">{e.symbol}</h3>
                    <p className="font-body text-white/60 text-[15px] leading-relaxed">{e.meaning}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function TrustedPresence() {
  const hasVideo = Boolean(FEATURED_VIDEO.youtubeId);
  return (
    <section id="presence" className="bg-white py-24 md:py-36">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="max-w-[720px] mb-14">
          <Reveal>
            <p className="overline text-[#B08D57] mb-6">Verified Presence</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="section-title font-display text-[#111] mb-6">
              Trusted Across Every Channel
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="font-body text-lg text-[#666] leading-relaxed">
              Find and verify AGRAGA Velora wherever business is done — our presence is open,
              consistent, and accountable.
            </p>
          </Reveal>
        </div>

        <div className="grid md:grid-cols-2 gap-10 lg:gap-16 items-start">
          {/* Video showcase */}
          <Reveal>
            <div className="relative aspect-video overflow-hidden border border-[#E7E1D6] bg-[#111]">
              {hasVideo ? (
                <iframe
                  className="w-full h-full"
                  src={`https://www.youtube.com/embed/${FEATURED_VIDEO.youtubeId}`}
                  title={FEATURED_VIDEO.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  data-testid="featured-video"
                />
              ) : (
                <div className="absolute inset-0" data-testid="video-placeholder">
                  <img src={IMAGES.kerala} alt="AGRAGA Velora film" className="w-full h-full object-cover opacity-60" />
                  <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center text-center px-6">
                    <span className="w-16 h-16 rounded-full bg-[#B08D57] flex items-center justify-center mb-5">
                      <Play size={22} className="text-white ml-1" fill="currentColor" />
                    </span>
                    <p className="font-display text-white text-2xl mb-2">{FEATURED_VIDEO.title}</p>
                    <p className="font-body text-white/70 text-sm max-w-[320px]">{FEATURED_VIDEO.caption}</p>
                  </div>
                </div>
              )}
            </div>
          </Reveal>

          {/* Links grid */}
          <div className="grid sm:grid-cols-2 gap-px bg-[#E7E1D6] border border-[#E7E1D6]">
            {TRUSTED_LINKS.map((l, i) => {
              const Icon = ICONS[l.icon] || ArrowUpRight;
              return (
                <Reveal key={l.label} delay={i * 0.05} className="bg-white">
                  <a
                    href={l.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`trusted-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="group flex items-center justify-between p-7 h-full transition-colors duration-300 hover:bg-[#111]"
                  >
                    <span className="flex items-center gap-4">
                      <Icon size={22} strokeWidth={1.5} className="text-[#B08D57]" />
                      <span className="font-head font-semibold text-[#111] transition-colors duration-300 group-hover:text-white">
                        {l.label}
                      </span>
                    </span>
                    <ArrowUpRight
                      size={18}
                      className="text-[#999] transition-all duration-300 group-hover:text-[#B08D57] group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                    />
                  </a>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
